import type { Adventure, ExpChoice, ExpItem, PixelArt } from './expedition.ts';

/** Colours for the pixel art below ('.' is clear). */
export const PIXEL_COLORS: Record<string, string> = {
  k: '#1a1a1a',
  w: '#f4f1e8',
  y: '#f2c94c',
  o: '#e8892b',
  r: '#c8372d',
  b: '#8b5a2b',
  d: '#5a3a1c',
  g: '#5aa83a',
  G: '#2f6b2a',
  u: '#4a90d9',
  U: '#2a4f8a',
  s: '#9a9aa0',
  S: '#5c5c64',
  c: '#7fd6e0',
  t: '#d9b77e',
};

/** The equipment. Slots and uses are per copy. */
export const ITEMS: ExpItem[] = [
  {
    id: 'torch',
    name: 'Torch',
    slots: 1,
    uses: 3,
    text: 'Lights up dark places.',
    icon: [
      '...yy...',
      '..yooy..',
      '..yooy..',
      '...bb...',
      '...bb...',
      '...bb...',
      '...bb...',
      '...dd...',
    ],
  },
  {
    id: 'blocks',
    name: 'Blocks',
    slots: 2,
    uses: 4,
    text: 'Build bridges and steps.',
    icon: [
      '........',
      'gggg....',
      'bbbb....',
      'bdbb....',
      'ggggssss',
      'bbbbsSss',
      'bdbbssSs',
      'bbbbssss',
    ],
  },
  {
    id: 'bread',
    name: 'Bread',
    slots: 1,
    uses: 1,
    restores: 3,
    text: 'Food: eat it for +3 energy.',
    icon: [
      '........',
      '........',
      '..tttt..',
      '.tbtbtt.',
      'tbtbtbtt',
      'tttttttt',
      '.dddddd.',
      '........',
    ],
  },
  {
    id: 'apple',
    name: 'Apple',
    slots: 1,
    uses: 1,
    restores: 1,
    text: 'Food: eat it for +1 energy.',
    icon: [
      '....d...',
      '...dg...',
      '.rrrrrr.',
      'rrwrrrrr',
      'rwrrrrrr',
      'rrrrrrrr',
      '.rrrrrr.',
      '..r..r..',
    ],
  },
  {
    id: 'pickaxe',
    name: 'Pickaxe',
    slots: 2,
    uses: 3,
    text: 'Breaks stone.',
    icon: [
      '.ssssss.',
      's..bb..s',
      '...bb...',
      '...bb...',
      '...bb...',
      '...bb...',
      '...dd...',
      '........',
    ],
  },
  {
    id: 'ladder',
    name: 'Ladder',
    slots: 2,
    uses: 2,
    text: 'Climb up or down steep places.',
    icon: [
      '.b....b.',
      '.bbbbbb.',
      '.b....b.',
      '.b....b.',
      '.bbbbbb.',
      '.b....b.',
      '.b....b.',
      '.bbbbbb.',
    ],
  },
  {
    id: 'boat',
    name: 'Boat',
    slots: 3,
    uses: 1,
    text: 'Crosses deep water.',
    icon: [
      '........',
      '........',
      '...b....',
      '...bw...',
      '...bww..',
      'bbbbbbbb',
      '.dbbbbd.',
      'uuuuuuuu',
    ],
  },
  {
    id: 'bottle',
    name: 'Bottle',
    slots: 1,
    uses: 2,
    text: 'Keeps small, delicate things safe.',
    icon: [
      '...dd...',
      '...ss...',
      '...cc...',
      '..cccc..',
      '.cwccwc.',
      '.cccccc.',
      '.cwcccc.',
      '..cccc..',
    ],
  },
  {
    id: 'shield',
    name: 'Shield',
    slots: 2,
    uses: 2,
    text: 'Keeps falling bits off you.',
    icon: [
      '.bbbbbb.',
      'bsbbbbsb',
      'bbbrrbbb',
      'bbrrrrbb',
      'bbbrrbbb',
      '.bbbbbb.',
      '..bbbb..',
      '...bb...',
    ],
  },
  {
    id: 'cloak',
    name: 'Warm cloak',
    slots: 2,
    uses: 2,
    text: 'Keeps out cold wind.',
    icon: [
      '..rrrr..',
      '.rr..rr.',
      '.rrrrrr.',
      'rrryyrrr',
      'rrrrrrrr',
      'rrrrrrrr',
      'rrrrrrrr',
      'r.r..r.r',
    ],
  },
  {
    id: 'map',
    name: 'Map',
    slots: 1,
    uses: 2,
    text: 'Shows the way to a place.',
    icon: [
      'tttttttt',
      'tuutgggt',
      'tuuggggt',
      'tggrrggt',
      'tgggrggt',
      'tgggrgut',
      'tgggguut',
      'tttttttt',
    ],
  },
  {
    id: 'compass',
    name: 'Compass',
    slots: 1,
    uses: 2,
    text: 'Always points the way home.',
    icon: [
      '..ssss..',
      '.swwwws.',
      'swwrwwws',
      'swwrwwws',
      'swwSwwws',
      'swwSwwws',
      '.swwwws.',
      '..ssss..',
    ],
  },
  {
    id: 'shears',
    name: 'Shears',
    slots: 1,
    uses: 3,
    text: 'Snips leaves, vines and thorns.',
    icon: [
      's.....s.',
      '.s...s..',
      '..s.s...',
      '...s....',
      '..r.r...',
      '.r...r..',
      '.r...r..',
      '..r.r...',
    ],
  },
  {
    id: 'shovel',
    name: 'Shovel',
    slots: 2,
    uses: 3,
    text: 'Digs through sand and snow.',
    icon: [
      '..ssss..',
      '..ssss..',
      '..ssss..',
      '...ss...',
      '...bb...',
      '...bb...',
      '...bb...',
      '..bddb..',
    ],
  },
  {
    id: 'tent',
    name: 'Tent',
    slots: 3,
    uses: 1,
    text: 'A cosy place to rest.',
    icon: [
      '...g....',
      '...gg...',
      '..gGgg..',
      '.gGGggg.',
      '.gGkkgg.',
      'gGGkkggg',
      'gGGkkggg',
      'bbbbbbbb',
    ],
  },
  {
    id: 'spyglass',
    name: 'Spyglass',
    slots: 1,
    uses: 2,
    text: 'Sees things far away.',
    icon: [
      '........',
      '........',
      'S.......',
      'SSyySSy.',
      'SSyySSyc',
      'SSyySSy.',
      'S.......',
      '........',
    ],
  },
];

/** Event pictures. */
export const SCENES: Record<string, PixelArt> = {
  dark: [
    'ssssssss',
    'ssSSSSss',
    'sSkkkkSs',
    'Skkkkkks',
    'Skkkkkks',
    'Skkkkkks',
    'Skkkkkks',
    'gggggggg',
  ],
  gap: [
    '........',
    '........',
    '........',
    'gggkkggg',
    'bbbkkbbb',
    'bdbkkbdb',
    'bbbkkbbb',
    'bbbkkbbb',
  ],
  water: [
    '........',
    '..g.....',
    'gggg....',
    'uuuuuuuu',
    'uUuuuUuu',
    'uuuUuuuU',
    'uuuuuuuu',
    'UuuuUuuu',
  ],
  plants: [
    '.G.G..G.',
    'GgGgGGgG',
    'gGgrgGgg',
    'GgGgGgrG',
    'gGrgGgGg',
    'GgGgGGgG',
    '.G.gG.G.',
    'bbbbbbbb',
  ],
  cold: [
    'w..w..w.',
    '..w..w..',
    'w...w..w',
    '...ss...',
    '..swws..',
    '.swwwws.',
    'swwwwwws',
    'wwwwwwww',
  ],
  cliff: [
    '....sss.',
    '...sSss.',
    '...sSss.',
    '..ssSss.',
    '..sSsss.',
    '.ssSsss.',
    '.sSssss.',
    'ggggssss',
  ],
  rocks: [
    'S.s..S..',
    '..s.s...',
    's..S..s.',
    '........',
    '..ss.S..',
    '.sSsssS.',
    'sssSsSss',
    'SsssssSs',
  ],
  sky: [
    'UUUwUUUU',
    'UwUUUUwU',
    'UUUUUUUU',
    'UUUUUwUU',
    'UwUUUUsU',
    'UUUUUUsU',
    'GGGGGGsG',
    'GGGGGGsG',
  ],
  treasure: [
    '..y..y..',
    'y.......',
    '.bbbbbb.',
    'bddyyddb',
    'bbbyybbb',
    'bddddddb',
    'bbbbbbbb',
    '........',
  ],
  slime: [
    '........',
    '........',
    '..gggg..',
    '.gGggGg.',
    '.gkggkg.',
    '.gggggg.',
    '.ggkkgg.',
    '..gggg..',
  ],
  fog: [
    'ssssssss',
    'swswswss',
    'ssGsswsw',
    'sGGGssss',
    'ssbswsws',
    'wsbsssss',
    'ssbsswss',
    'gggggggg',
  ],
  /** Shown on safe choices that need no items. */
  walk: [
    '........',
    '..dd....',
    '..bb....',
    '..bb....',
    '..bbbb..',
    '.bbbbbb.',
    '.dddddd.',
    '........',
  ],
  sand: [
    '........',
    '...ss...',
    '..ssss..',
    '.ssSSss.',
    'tttttttt',
    'ttottttt',
    'tttttott',
    'tttttttt',
  ],
};

const c = (
  id: string,
  label: string,
  use: ExpChoice['use'],
  energy: number,
  points: number,
  result: string,
): ExpChoice => ({ id, label, use, energy, points, result });

/**
 * Every event has a best choice (3 points, needs the right item), sometimes a second-best item
 * choice (2), and a safe choice with no items (1 point, more tiring). "Head home" is added by the
 * engine. Forecast lines name each event's need, so nothing is a surprise.
 */
export const ADVENTURES: Adventure[] = [
  {
    id: 'glow-cave',
    version: 1,
    title: 'Glow Cave',
    objective: 'Go into the cave, cross the gap, and bring back a glowing mushroom.',
    forecast: [
      'The tunnel is very dark.',
      'A gap two blocks wide cuts across the path.',
      'The mushroom is soft and squishes easily.',
      'Long walk: you start with only 2 energy.',
    ],
    shelf: ['torch', 'blocks', 'bread', 'apple', 'bottle', 'boat', 'shovel'],
    startEnergy: 2,
    gold: 9,
    events: [
      {
        id: 'dark',
        title: 'Into the dark',
        text: 'The tunnel goes very dark. You can’t see your feet!',
        scene: 'dark',
        tip: 'Pack a torch — the tunnel is dark.',
        choices: [
          c('torch', 'Light a torch', { torch: 1 }, -1, 3, 'The torch lit the way. Easy!'),
          c(
            'feel',
            'Feel along the wall, step by step',
            {},
            -2,
            1,
            'Slow and tiring, but you got through.',
          ),
        ],
      },
      {
        id: 'gap',
        title: 'A gap!',
        text: 'A gap two blocks wide. Too far to jump.',
        scene: 'gap',
        tip: 'Pack blocks to bridge the gap. You need 2.',
        choices: [
          c(
            'bridge',
            'Build a bridge with 2 blocks',
            { blocks: 2 },
            -1,
            3,
            'Your bridge held. Across you go!',
          ),
          c(
            'around',
            'Climb down and up the other side',
            {},
            -2,
            1,
            'A long, tiring climb, but you made it.',
          ),
        ],
      },
      {
        id: 'mushroom',
        title: 'The glowing mushroom',
        text: 'There it is! It is soft and squishy.',
        scene: 'treasure',
        tip: 'Pack a bottle to keep the mushroom safe.',
        choices: [
          c(
            'bottle',
            'Put it in a bottle',
            { bottle: 1 },
            -1,
            3,
            'Safe in the bottle, still glowing.',
          ),
          c(
            'hands',
            'Carry it in your hands, very gently',
            {},
            -1,
            1,
            'It got a bit squashed, but you brought it back.',
          ),
        ],
      },
    ],
    solutions: [
      {
        loadout: { torch: 1, blocks: 1, bottle: 1, bread: 1 },
        actions: [
          { choose: 'torch' },
          { eat: 'bread' },
          { choose: 'bridge' },
          { choose: 'bottle' },
        ],
        medal: 'gold',
      },
      {
        loadout: { torch: 1, bottle: 1, bread: 2 },
        actions: [
          { choose: 'torch' },
          { eat: 'bread' },
          { choose: 'around' },
          { choose: 'bottle' },
        ],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'berry-island',
    version: 1,
    title: 'Berry Island',
    objective: 'Cross to the island, pick sweet berries, and get home at sunset.',
    forecast: [
      'Deep water all around the island.',
      'The berries grow in tangled, prickly bushes.',
      'The sun sets on the way home.',
    ],
    shelf: ['boat', 'shears', 'torch', 'compass', 'apple', 'bread', 'pickaxe'],
    startEnergy: 3,
    gold: 9,
    events: [
      {
        id: 'river',
        title: 'The wide river',
        text: 'The island is across deep water.',
        scene: 'water',
        tip: 'Pack a boat to cross the river.',
        choices: [
          c('boat', 'Row across in your boat', { boat: 1 }, -1, 3, 'A smooth ride to the island.'),
          c(
            'ford',
            'Walk far upstream to the stepping stones',
            {},
            -2,
            1,
            'A long walk, but you got across.',
          ),
        ],
      },
      {
        id: 'bushes',
        title: 'Prickly bushes',
        text: 'The berries are deep inside prickly bushes.',
        scene: 'plants',
        tip: 'Pack shears for the prickly bushes.',
        choices: [
          c(
            'shears',
            'Snip a way in with shears',
            { shears: 1 },
            -1,
            3,
            'Snip, snip — a whole basket of berries!',
          ),
          c(
            'slow',
            'Reach in very slowly around the prickles',
            {},
            -2,
            1,
            'Only a handful, but they taste great.',
          ),
        ],
      },
      {
        id: 'sunset',
        title: 'Getting dark',
        text: 'The sun is setting on the way home.',
        scene: 'sky',
        tip: 'Pack a torch for the walk home at sunset.',
        choices: [
          c(
            'torch',
            'Light a torch to see the path',
            { torch: 1 },
            0,
            3,
            'The path glowed all the way home.',
          ),
          c(
            'compass',
            'Follow your compass home',
            { compass: 1 },
            -1,
            2,
            'The compass found home, but it was hard to see.',
          ),
          c('slow', 'Walk home slowly in the dusk', {}, -2, 1, 'Slow steps, but you got home.'),
        ],
      },
    ],
    solutions: [
      {
        loadout: { boat: 1, shears: 1, torch: 1 },
        actions: [{ choose: 'boat' }, { choose: 'shears' }, { choose: 'torch' }],
        medal: 'gold',
      },
      {
        loadout: { boat: 1, shears: 1, compass: 1 },
        actions: [{ choose: 'boat' }, { choose: 'shears' }, { choose: 'compass' }],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'snowy-peak',
    version: 1,
    title: 'Snowy Peak',
    objective: 'Climb the snowy mountain and plant a flag at the very top.',
    forecast: [
      'An icy wind blows on the slope.',
      'A big snow drift blocks the path.',
      'The top is up a steep cliff.',
      'Cold air: you start with only 2 energy.',
    ],
    shelf: ['cloak', 'shovel', 'ladder', 'blocks', 'bread', 'apple', 'boat'],
    startEnergy: 2,
    gold: 9,
    events: [
      {
        id: 'wind',
        title: 'Icy wind',
        text: 'A freezing wind blows down the slope.',
        scene: 'cold',
        tip: 'Pack a warm cloak for the icy wind.',
        choices: [
          c('cloak', 'Wrap up in your warm cloak', { cloak: 1 }, 0, 3, 'Snug and warm. On you go!'),
          c(
            'hide',
            'Hide behind rocks until it calms down',
            {},
            -1,
            1,
            'Brrr! It took a while, but the wind stopped.',
          ),
        ],
      },
      {
        id: 'drift',
        title: 'Snow drift',
        text: 'A huge pile of snow blocks the path.',
        scene: 'cold',
        tip: 'Pack a shovel to dig through the snow.',
        choices: [
          c(
            'shovel',
            'Dig through with a shovel',
            { shovel: 1 },
            -1,
            3,
            'A neat tunnel through the snow.',
          ),
          c(
            'blocks',
            'Build steps over it with 2 blocks',
            { blocks: 2 },
            -1,
            2,
            'Up and over the drift.',
          ),
          c('around', 'Walk the long way around', {}, -2, 1, 'A long way, but you got past.'),
        ],
      },
      {
        id: 'cliff',
        title: 'The last cliff',
        text: 'The top is up a steep cliff. Plant your flag there!',
        scene: 'cliff',
        tip: 'Pack a ladder for the steep cliff.',
        choices: [
          c('ladder', 'Climb up a ladder', { ladder: 1 }, -1, 3, 'Up you go — the flag is flying!'),
          c(
            'blocks',
            'Build a tower of 3 blocks',
            { blocks: 3 },
            -1,
            2,
            'A wobbly tower, but the flag is up.',
          ),
          c(
            'zigzag',
            'Take the long zigzag trail',
            {},
            -2,
            1,
            'Zig, zag, zig… the flag is up at last.',
          ),
        ],
      },
    ],
    solutions: [
      {
        loadout: { cloak: 1, shovel: 1, ladder: 1 },
        actions: [{ choose: 'cloak' }, { choose: 'shovel' }, { choose: 'ladder' }],
        medal: 'gold',
      },
      {
        loadout: { cloak: 1, blocks: 2 },
        actions: [{ choose: 'cloak' }, { choose: 'blocks' }, { choose: 'blocks' }],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'sand-temple',
    version: 1,
    title: 'Sand Temple',
    objective: 'Dig into the old temple and find the gold nugget inside.',
    forecast: [
      'Sand covers the temple door.',
      'The hall inside is dark.',
      'Fallen stones block the treasure room.',
      'Hot sun: you start with only 2 energy.',
    ],
    shelf: ['shovel', 'torch', 'pickaxe', 'bottle', 'bread', 'apple', 'cloak'],
    startEnergy: 2,
    gold: 9,
    events: [
      {
        id: 'door',
        title: 'Buried door',
        text: 'Sand covers the temple door.',
        scene: 'sand',
        tip: 'Pack a shovel to dig out the door.',
        choices: [
          c(
            'shovel',
            'Dig the sand away with a shovel',
            { shovel: 1 },
            -1,
            3,
            'The door is clear!',
          ),
          c(
            'hands',
            'Scoop the sand with your hands',
            {},
            -2,
            1,
            'Phew, hard work — but the door is open.',
          ),
        ],
      },
      {
        id: 'hall',
        title: 'Dark hall',
        text: 'Inside, it is dark and quiet.',
        scene: 'dark',
        tip: 'Pack a torch for the dark hall.',
        choices: [
          c(
            'torch',
            'Light a torch',
            { torch: 1 },
            0,
            3,
            'Pictures on the walls! The way is clear.',
          ),
          c(
            'wait',
            'Wait for your eyes to get used to the dark',
            {},
            -1,
            1,
            'After a while you could see enough.',
          ),
        ],
      },
      {
        id: 'stones',
        title: 'Stone pile',
        text: 'Fallen stones block the room with the gold nugget.',
        scene: 'rocks',
        tip: 'Pack a pickaxe to clear the stones.',
        choices: [
          c(
            'pickaxe',
            'Break the stones with a pickaxe',
            { pickaxe: 1 },
            -1,
            3,
            'Crack! The gold nugget is yours.',
          ),
          c(
            'squeeze',
            'Squeeze through a tiny gap',
            {},
            -2,
            1,
            'A tight squeeze, but you reached the nugget.',
          ),
        ],
      },
    ],
    solutions: [
      {
        loadout: { shovel: 1, torch: 1, pickaxe: 1 },
        actions: [{ choose: 'shovel' }, { choose: 'torch' }, { choose: 'pickaxe' }],
        medal: 'gold',
      },
      {
        loadout: { shovel: 1, torch: 1, bread: 2 },
        actions: [
          { choose: 'shovel' },
          { choose: 'torch' },
          { eat: 'bread' },
          { choose: 'squeeze' },
        ],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'foggy-forest',
    version: 1,
    title: 'Foggy Forest',
    objective: 'Find the old oak tree in the fog, hang a birdhouse, and come back.',
    forecast: [
      'Thick fog: you need to know where the oak tree is.',
      'Vines hang across the trail.',
      'Finding the way home in fog is tricky.',
    ],
    shelf: ['map', 'compass', 'shears', 'torch', 'apple', 'bread', 'spyglass'],
    startEnergy: 3,
    gold: 9,
    events: [
      {
        id: 'fog',
        title: 'Thick fog',
        text: 'You can only see a few steps. Which way is the old oak?',
        scene: 'fog',
        tip: 'Pack a map to find the old oak tree.',
        choices: [
          c('map', 'Check your map', { map: 1 }, -1, 3, 'The map showed the way to the oak.'),
          c(
            'stream',
            'Follow the stream until you find it',
            {},
            -2,
            1,
            'A long walk, but there was the oak!',
          ),
        ],
      },
      {
        id: 'vines',
        title: 'Hanging vines',
        text: 'Thick vines hang across the trail.',
        scene: 'plants',
        tip: 'Pack shears to snip the vines.',
        choices: [
          c(
            'shears',
            'Snip the vines',
            { shears: 1 },
            -1,
            3,
            'Snip! The birdhouse is up in the oak.',
          ),
          c('push', 'Push through the vines slowly', {}, -2, 1, 'Tangly! But the birdhouse is up.'),
        ],
      },
      {
        id: 'home',
        title: 'Find your way back',
        text: 'Now, which way is home in all this fog?',
        scene: 'fog',
        tip: 'Pack a compass to find your way home.',
        choices: [
          c('compass', 'Follow your compass', { compass: 1 }, 0, 3, 'Straight home, no trouble.'),
          c('map', 'Read your map again', { map: 1 }, -1, 2, 'The map got you home, slowly.'),
          c(
            'tracks',
            'Follow your own footprints back slowly',
            {},
            -2,
            1,
            'Step by step, all the way home.',
          ),
        ],
      },
    ],
    solutions: [
      {
        loadout: { map: 1, shears: 1, compass: 1 },
        actions: [{ choose: 'map' }, { choose: 'shears' }, { choose: 'compass' }],
        medal: 'gold',
      },
      {
        loadout: { map: 1, shears: 1, apple: 1 },
        actions: [{ choose: 'map' }, { choose: 'shears' }, { choose: 'map' }],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'starry-hill',
    version: 1,
    title: 'Starry Hill',
    objective: 'Camp on the hill, watch the shooting stars, and spot the far tower’s flag.',
    forecast: [
      'It gets dark fast on the way up.',
      'The hilltop is cold and windy.',
      'The tower is very far away.',
      'Steep path: you start with only 2 energy.',
    ],
    shelf: ['torch', 'tent', 'cloak', 'spyglass', 'bread', 'map', 'boat'],
    startEnergy: 2,
    gold: 9,
    events: [
      {
        id: 'dusk',
        title: 'Night falls',
        text: 'It gets dark fast on the way up the hill.',
        scene: 'sky',
        tip: 'Pack a torch for the dark path.',
        choices: [
          c('torch', 'Light a torch', { torch: 1 }, 0, 3, 'Up the hill in torchlight.'),
          c('moon', 'Walk slowly by moonlight', {}, -1, 1, 'Slow, but you reached the top.'),
        ],
      },
      {
        id: 'camp',
        title: 'Windy hilltop',
        text: 'It is cold and windy at the top.',
        scene: 'cold',
        tip: 'Pack a tent to rest on the windy hilltop.',
        choices: [
          c(
            'tent',
            'Set up your tent and rest',
            { tent: 1 },
            2,
            3,
            'Cosy! You feel rested (+2 energy).',
          ),
          c('cloak', 'Wrap up in your cloak', { cloak: 1 }, 0, 2, 'Warm enough to stay.'),
          c('huddle', 'Huddle behind a rock', {}, -1, 1, 'Chilly, but you stayed for the stars.'),
        ],
      },
      {
        id: 'tower',
        title: 'The far tower',
        text: 'Shooting stars! Far away is a tiny tower. What colour is its flag?',
        scene: 'sky',
        tip: 'Pack a spyglass to see the far tower.',
        choices: [
          c(
            'spyglass',
            'Look through your spyglass',
            { spyglass: 1 },
            -1,
            3,
            'The flag is red! You saw it clearly.',
          ),
          c(
            'squint',
            'Squint as hard as you can',
            {},
            -1,
            1,
            'Lovely stars, but the tower was too far.',
          ),
        ],
      },
    ],
    solutions: [
      {
        loadout: { torch: 1, tent: 1, spyglass: 1 },
        actions: [{ choose: 'torch' }, { choose: 'tent' }, { choose: 'spyglass' }],
        medal: 'gold',
      },
      {
        loadout: { torch: 1, cloak: 1, spyglass: 1 },
        actions: [{ choose: 'torch' }, { choose: 'cloak' }, { choose: 'spyglass' }],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'deep-mine',
    version: 1,
    title: 'Deep Mine',
    objective: 'Climb down the old mine, dig out shiny ore, and get back out.',
    forecast: [
      'The mine ladder is missing rungs.',
      'The ore is stuck in hard stone.',
      'Little stones slide down near the exit.',
      'Lanterns already light the tunnels.',
    ],
    shelf: ['ladder', 'blocks', 'pickaxe', 'shield', 'torch', 'bread', 'apple'],
    startEnergy: 3,
    gold: 9,
    events: [
      {
        id: 'down',
        title: 'Broken ladder',
        text: 'The old mine ladder is missing rungs.',
        scene: 'cliff',
        tip: 'Pack your own ladder to climb down.',
        choices: [
          c(
            'ladder',
            'Hook on your own ladder and climb down',
            { ladder: 1 },
            -1,
            3,
            'Down you go, safe and steady.',
          ),
          c(
            'blocks',
            'Build steps down with 3 blocks',
            { blocks: 3 },
            -1,
            2,
            'Block steps all the way down.',
          ),
          c(
            'rocks',
            'Climb down slowly using the rocks',
            {},
            -2,
            1,
            'Slowly, slowly… you reached the bottom.',
          ),
        ],
      },
      {
        id: 'ore',
        title: 'Shiny ore',
        text: 'Shiny ore is stuck in hard stone.',
        scene: 'treasure',
        tip: 'Pack a pickaxe to dig out the ore.',
        choices: [
          c('pickaxe', 'Dig it out with a pickaxe', { pickaxe: 1 }, -1, 3, 'A big shiny chunk!'),
          c(
            'floor',
            'Look for small loose bits on the floor',
            {},
            -2,
            1,
            'A few small bits — still shiny!',
          ),
        ],
      },
      {
        id: 'gravel',
        title: 'Sliding stones',
        text: 'Near the exit, little stones start to slide down.',
        scene: 'rocks',
        tip: 'Pack a shield for the sliding stones.',
        choices: [
          c(
            'shield',
            'Hold up your shield and walk out',
            { shield: 1 },
            0,
            3,
            'Plink, plink — out into the sunshine.',
          ),
          c('wait', 'Wait for it to stop, then go', {}, -1, 1, 'After a long wait, out you came.'),
        ],
      },
    ],
    solutions: [
      {
        loadout: { ladder: 1, pickaxe: 1, shield: 1 },
        actions: [{ choose: 'ladder' }, { choose: 'pickaxe' }, { choose: 'shield' }],
        medal: 'gold',
      },
      {
        loadout: { blocks: 1, pickaxe: 1, shield: 1 },
        actions: [{ choose: 'blocks' }, { choose: 'pickaxe' }, { choose: 'shield' }],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'swamp-seed',
    version: 1,
    title: 'Swamp Seed',
    objective: 'Cross the swamp and bring home a rare flower seed.',
    forecast: [
      'Deep, muddy water on the way.',
      'A friendly slime bounces on the path. It loves apples!',
      'The seed is tiny and easy to drop.',
    ],
    shelf: ['boat', 'blocks', 'apple', 'shield', 'bottle', 'bread', 'ladder'],
    startEnergy: 3,
    gold: 9,
    events: [
      {
        id: 'swamp',
        title: 'Muddy swamp',
        text: 'Deep, muddy water lies between you and the flower.',
        scene: 'water',
        tip: 'Pack a boat for the muddy water.',
        choices: [
          c('boat', 'Paddle across in your boat', { boat: 1 }, -1, 3, 'Paddle, paddle — across!'),
          c(
            'blocks',
            'Make a path with 3 blocks',
            { blocks: 3 },
            -1,
            2,
            'A block path across the mud.',
          ),
          c(
            'edge',
            'Walk the long way round the edge',
            {},
            -2,
            1,
            'Muddy boots, but you got there.',
          ),
        ],
      },
      {
        id: 'slime',
        title: 'A bouncy slime',
        text: 'A friendly slime is bouncing on the path. It loves apples.',
        scene: 'slime',
        tip: 'Pack an apple for the slime.',
        choices: [
          c('apple', 'Give it an apple', { apple: 1 }, 0, 3, 'Boing! It bounced away happy.'),
          c(
            'shield',
            'Gently nudge it aside with your shield',
            { shield: 1 },
            -1,
            2,
            'Squish… it wobbled out of the way.',
          ),
          c(
            'wait',
            'Wait until it bounces off by itself',
            {},
            -2,
            1,
            'Boing… boing… it finally left.',
          ),
        ],
      },
      {
        id: 'seed',
        title: 'The rare flower',
        text: 'The flower has one tiny seed. It’s easy to drop!',
        scene: 'plants',
        tip: 'Pack a bottle to keep the seed safe.',
        choices: [
          c(
            'bottle',
            'Keep the seed safe in a bottle',
            { bottle: 1 },
            0,
            3,
            'Safe in the bottle, all the way home.',
          ),
          c(
            'hand',
            'Hold it tight in your hand',
            {},
            -1,
            1,
            'Your hand got tired, but the seed made it.',
          ),
        ],
      },
    ],
    solutions: [
      {
        loadout: { boat: 1, apple: 1, bottle: 1 },
        actions: [{ choose: 'boat' }, { choose: 'apple' }, { choose: 'bottle' }],
        medal: 'gold',
      },
      {
        loadout: { blocks: 1, shield: 1, bottle: 1 },
        actions: [{ choose: 'blocks' }, { choose: 'shield' }, { choose: 'bottle' }],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'lily-pond',
    version: 1,
    title: 'Lily Pond Picnic',
    objective: 'Cross the pond to the little island, have a picnic, and get home after dark.',
    forecast: [
      'The island is across deep water.',
      'Rain clouds are coming. You will need shelter.',
      'It will be dark on the way home.',
      'Easy trip: you start with 4 energy.',
    ],
    shelf: ['boat', 'blocks', 'tent', 'cloak', 'torch', 'compass', 'bread'],
    startEnergy: 4,
    gold: 9,
    events: [
      {
        id: 'pond',
        title: 'Lily pond',
        text: 'The island is across deep water. Frogs sit on the lily pads.',
        scene: 'water',
        tip: 'Pack a boat to cross the pond.',
        choices: [
          c(
            'boat',
            'Row across in your boat',
            { boat: 1 },
            -1,
            3,
            'You rowed past the frogs. Ribbit!',
          ),
          c(
            'blocks',
            'Build a path with 3 blocks',
            { blocks: 3 },
            -1,
            2,
            'A wobbly block path, but you got across.',
          ),
          c(
            'pads',
            'Hop along the big lily pads',
            {},
            -2,
            1,
            'Splashy and tiring, but you made it.',
          ),
        ],
      },
      {
        id: 'rain',
        title: 'Picnic rain',
        text: 'Plip, plop! It starts to rain on your picnic.',
        scene: 'fog',
        tip: 'Pack a tent for the rain.',
        choices: [
          c(
            'tent',
            'Put up your tent and eat inside',
            { tent: 1 },
            1,
            3,
            'Dry and cosy! You feel rested (+1 energy).',
          ),
          c('cloak', 'Wrap up in your cloak', { cloak: 1 }, 0, 2, 'A bit soggy, but warm.'),
          c('tree', 'Squeeze under a tree', {}, -1, 1, 'Drippy, but the sandwiches were fine.'),
        ],
      },
      {
        id: 'dusk',
        title: 'Home after dark',
        text: 'The rain has stopped, but now it is dark.',
        scene: 'sky',
        tip: 'Pack a torch for the dark walk home.',
        choices: [
          c(
            'torch',
            'Light a torch',
            { torch: 1 },
            0,
            3,
            'The torch lit the path. Home in time for bed!',
          ),
          c(
            'compass',
            'Follow your compass',
            { compass: 1 },
            -1,
            2,
            'The compass knew the way, step by step.',
          ),
          c('slow', 'Feel your way home slowly', {}, -2, 1, 'Slow and tiring, but you got home.'),
        ],
      },
    ],
    solutions: [
      {
        loadout: { boat: 1, tent: 1, torch: 1 },
        actions: [{ choose: 'boat' }, { choose: 'tent' }, { choose: 'torch' }],
        medal: 'gold',
      },
      {
        loadout: { blocks: 1, cloak: 1, compass: 1 },
        actions: [{ choose: 'blocks' }, { choose: 'cloak' }, { choose: 'compass' }],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'sparkle-canyon',
    version: 1,
    title: 'Sparkle Canyon',
    objective: 'Climb down into the canyon, bring back a blue crystal, and climb out again.',
    forecast: [
      'The canyon wall is steep.',
      'The crystal is stuck in hard stone.',
      'Pebbles fall when you climb back up the wall.',
      'Hot day: you start with only 2 energy.',
    ],
    shelf: ['ladder', 'blocks', 'pickaxe', 'shovel', 'shield', 'bread', 'apple'],
    startEnergy: 2,
    gold: 9,
    events: [
      {
        id: 'wall',
        title: 'Steep wall',
        text: 'The canyon wall goes straight down.',
        scene: 'cliff',
        tip: 'Pack a ladder for the steep wall.',
        choices: [
          c(
            'ladder',
            'Climb down your ladder',
            { ladder: 1 },
            -1,
            3,
            'Down, down, down — safe at the bottom.',
          ),
          c(
            'blocks',
            'Build steps down with 3 blocks',
            { blocks: 3 },
            -1,
            2,
            'Block steps all the way down.',
          ),
          c(
            'slide',
            'Slide down the sandy slope',
            {},
            -2,
            1,
            'Whoosh! Sandy and tiring, but you are down.',
          ),
        ],
      },
      {
        id: 'crystal',
        title: 'The blue crystal',
        text: 'It sparkles in the rock. The rock is very hard.',
        scene: 'treasure',
        tip: 'Pack a pickaxe for the hard rock.',
        choices: [
          c(
            'pickaxe',
            'Tap it free with a pickaxe',
            { pickaxe: 1 },
            -1,
            3,
            'Tink! A big blue crystal!',
          ),
          c(
            'shovel',
            'Dig the sand around it with a shovel',
            { shovel: 1 },
            -1,
            2,
            'Scrape, scrape… a small crystal came loose.',
          ),
          c(
            'look',
            'Look for a loose crystal on the ground',
            {},
            -2,
            1,
            'A tiny one, but still sparkly!',
          ),
        ],
      },
      {
        id: 'pebbles',
        title: 'Falling pebbles',
        text: 'Time to climb out. Pebbles bounce down the wall!',
        scene: 'rocks',
        tip: 'Pack a shield. Your ladder has 2 climbs: one down, one up.',
        choices: [
          c(
            'climb',
            'Climb your ladder with your shield up',
            { shield: 1, ladder: 1 },
            -1,
            3,
            'Plink, plink — out of the canyon!',
          ),
          c(
            'shield',
            'Climb the rocks with your shield up',
            { shield: 1 },
            -2,
            2,
            'A hard climb, but no bumps on your head.',
          ),
          c(
            'wait',
            'Wait for the pebbles to stop, then climb',
            {},
            -2,
            1,
            'A long wait and a tiring climb, but you are out.',
          ),
        ],
      },
    ],
    solutions: [
      {
        loadout: { ladder: 1, pickaxe: 1, shield: 1, bread: 1 },
        actions: [
          { choose: 'ladder' },
          { choose: 'pickaxe' },
          { eat: 'bread' },
          { choose: 'climb' },
        ],
        medal: 'gold',
      },
      {
        loadout: { blocks: 1, pickaxe: 1, shield: 1, bread: 1 },
        actions: [
          { choose: 'blocks' },
          { choose: 'pickaxe' },
          { eat: 'bread' },
          { choose: 'shield' },
        ],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'echo-bell',
    version: 1,
    title: 'Echo Bell Caves',
    objective: 'Go deep into the caves, ring the old echo bell, and find your way out.',
    forecast: [
      'The way in is dark.',
      'An underground lake is in front of the bell.',
      'The way out is long and dark: it needs 2 torch lights.',
      'Cave air: you start with only 2 energy.',
    ],
    shelf: ['torch', 'boat', 'blocks', 'map', 'bread', 'apple', 'spyglass'],
    startEnergy: 2,
    gold: 9,
    events: [
      {
        id: 'dark',
        title: 'Dark tunnel',
        text: 'Drip, drip. The tunnel in is very dark.',
        scene: 'dark',
        tip: 'Pack a torch. One torch gives 3 lights.',
        choices: [
          c('torch', 'Light a torch', { torch: 1 }, -1, 3, 'Torchlight! Shiny rocks everywhere.'),
          c('feel', 'Feel along the wall', {}, -2, 1, 'Slow and tiring, but you got through.'),
        ],
      },
      {
        id: 'lake',
        title: 'Underground lake',
        text: 'Still, black water. The bell hangs on the other side.',
        scene: 'water',
        tip: 'Pack a boat for the lake.',
        choices: [
          c(
            'boat',
            'Row across in your boat',
            { boat: 1 },
            -1,
            3,
            'Dong! You rang the bell. Dong… dong… dong…',
          ),
          c(
            'blocks',
            'Build a path with 4 blocks',
            { blocks: 4 },
            -1,
            2,
            'Across on blocks. Dong! goes the bell.',
          ),
          c(
            'edge',
            'Creep along the slippery edge',
            {},
            -2,
            1,
            'Careful steps… Dong! You rang it.',
          ),
        ],
      },
      {
        id: 'out',
        title: 'The long way out',
        text: 'Lots of twisty tunnels, all dark. Which way is out?',
        scene: 'dark',
        tip: 'Save torch lights for the way out: it needs 2.',
        choices: [
          c(
            'torch',
            'Light 2 torches and follow the breeze',
            { torch: 2 },
            -1,
            3,
            'Bright and breezy — out into the sunshine!',
          ),
          c(
            'map',
            'Follow your map in the dark',
            { map: 1 },
            -2,
            2,
            'Bump, bump… but the map found the way out.',
          ),
          c(
            'echo',
            'Shout and follow your echo',
            {},
            -2,
            1,
            'Hello… hello… a long way, but you got out.',
          ),
        ],
      },
    ],
    solutions: [
      {
        loadout: { torch: 1, boat: 1, bread: 1 },
        actions: [{ choose: 'torch' }, { eat: 'bread' }, { choose: 'boat' }, { choose: 'torch' }],
        medal: 'gold',
      },
      {
        loadout: { torch: 1, blocks: 1, bread: 1 },
        actions: [{ choose: 'torch' }, { eat: 'bread' }, { choose: 'blocks' }, { choose: 'torch' }],
        medal: 'solved',
      },
    ],
  },
  {
    id: 'windy-lighthouse',
    version: 1,
    title: 'Windy Lighthouse',
    objective: 'Row out to the old lighthouse, climb to the lamp, and stay the stormy night.',
    forecast: [
      'Big waves between you and the lighthouse.',
      'The stairs are broken in 3 places.',
      'A cold, stormy night at the top.',
      'Long trip: you start with only 2 energy.',
    ],
    shelf: ['boat', 'ladder', 'blocks', 'tent', 'cloak', 'shield', 'bread', 'apple'],
    startEnergy: 2,
    gold: 9,
    events: [
      {
        id: 'waves',
        title: 'Big waves',
        text: 'The lighthouse is on a rock out at sea. The waves are big!',
        scene: 'water',
        tip: 'Pack a boat — and food, because rowing in waves is tiring.',
        choices: [
          c(
            'boat',
            'Row hard through the waves',
            { boat: 1 },
            -2,
            3,
            'Up and down the waves — you made it!',
          ),
          c(
            'wade',
            'Wade over the slippery rocks',
            {},
            -2,
            1,
            'Splash, slip, splash… tiring, but you got there.',
          ),
        ],
      },
      {
        id: 'stairs',
        title: 'Broken stairs',
        text: 'The stairs inside are broken in 3 places.',
        scene: 'cliff',
        tip: 'Pack 2 ladders: one ladder gives 2 climbs, and there are 3 broken bits.',
        choices: [
          c(
            'ladder',
            'Hook a ladder on each broken bit',
            { ladder: 3 },
            -1,
            3,
            'Up, up, up — you reached the lamp!',
          ),
          c(
            'blocks',
            'Fill the gaps with 4 blocks',
            { blocks: 4 },
            -1,
            2,
            'Block by block, up to the lamp.',
          ),
          c(
            'rail',
            'Climb up the old railing slowly',
            {},
            -2,
            1,
            'Wobbly and tiring, but you got to the top.',
          ),
        ],
      },
      {
        id: 'storm',
        title: 'Stormy night',
        text: 'Wind and rain! The lamp room is cold. Stay until morning.',
        scene: 'cold',
        tip: 'Pack a tent for the stormy night.',
        choices: [
          c(
            'tent',
            'Set up your tent by the big lamp',
            { tent: 1 },
            1,
            3,
            'Snug all night, and the lamp shone for the ships!',
          ),
          c('cloak', 'Wrap up in your cloak', { cloak: 1 }, 0, 2, 'Warm enough to stay the night.'),
          c('huddle', 'Huddle in a corner', {}, -1, 1, 'A chilly night, but morning came.'),
        ],
      },
    ],
    solutions: [
      {
        loadout: { boat: 1, ladder: 2, tent: 1, bread: 1 },
        actions: [{ eat: 'bread' }, { choose: 'boat' }, { choose: 'ladder' }, { choose: 'tent' }],
        medal: 'gold',
      },
      {
        loadout: { boat: 1, blocks: 1, cloak: 1, bread: 1 },
        actions: [{ eat: 'bread' }, { choose: 'boat' }, { choose: 'blocks' }, { choose: 'cloak' }],
        medal: 'solved',
      },
    ],
  },
];
